import type { Project, Settings, TeamMember, Article, Page, Associate, Service, AboutContent, Certificate, Testimonial, Video } from './types';

export const MOCK_PROJECTS: Project[] = [];
export const MOCK_TEAM_MEMBERS: TeamMember[] = [];
export const MOCK_ARTICLES: Article[] = [];

export const INITIAL_SETTINGS: Settings = {
    companyName: "ARCH.",
    slogan: "Crafting Spaces, Inspiring Life.",
    logoUrl: "",
    logoWidth: 120,
    address: "123 Architecture Lane, Metropolis, 54321",
    email: "contact@arch.design",
    phone: "(555) 123-4567",
    googleMapsUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d25516.33232876116!2d101.70281464379854!3d3.155403734035145!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31cc49c402137151%3A0x4424160e1e6b34!2sPetronas%20Twin%2Towers!5e0!3m2!1sen!2smy!4v1678886565458!5m2!1sen!2smy",
    formspreeEndpoint: "",
    emailService: 'formspree',
    resendSenderEmail: '',
    footerContent: "Minimalist Designs for a Modern World",
    projectCategories: [
        "Residential",
        "Infrastructure",
        "Cultural",
        "Corporate"
    ],
    socialLinks: [
        { platform: 'Twitter', url: 'https://twitter.com' },
        { platform: 'LinkedIn', url: 'https://linkedin.com' },
        { platform: 'Facebook', url: 'https://facebook.com' },
    ],
    theme: {
        frontendTheme: {
            primaryColor: '#111111',
            secondaryColor: '#F9F9F9',
            pageBackgroundColor: '#FFFFFF',
            headerBackgroundColor: '#111111',
            footerBackgroundColor: '#F9F9F9',
            navLinkColor: '#9CA3AF',
            activeLinkColor: '#FFFFFF',
            navLinkHoverColor: '#FFFFFF',
            fontFamily: {
                header: 'Inter',
                heading: 'Playfair Display',
                body: 'Inter',
            },
        },
        backendTheme: {
            adminSidebarColor: '#1f2937', // equivalent to bg-gray-800
            adminIconColor: '#e5e7eb', // equivalent to text-gray-200
        }
    },
    seo: {
        title: "ARCH. | Modern Architectural Design",
        description: "ARCH. is a design-focused architectural firm specializing in minimalist and contemporary spaces that inspire and endure.",
        favicon: "/favicon.svg"
    },
    openGraph: {
        title: "ARCH. | Crafting Spaces",
        description: "Explore the portfolio of ARCH., a leading firm in modern, minimalist architectural design.",
        imageUrl: ""
    },
    portfolioLayout: 'grid',
    detailLayout: 'classic',
    enableYearFilter: true,
    enableStatusFilter: true,
    developer: {
        projectsIcon: { svg: `<rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>`, viewBox: "0 0 24 24" },
        associatesIcon: { svg: `<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`, viewBox: "0 0 24 24" },
        journalIcon: { svg: `<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>`, viewBox: "0 0 24 24" },
        videosIcon: { svg: `<polygon points="5 3 19 12 5 21 5 3"></polygon>`, viewBox: "0 0 24 24" },
        pagesIcon: { svg: `<path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>`, viewBox: "0 0 24 24" },
        aboutIcon: { svg: `<circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />`, viewBox: "0 0 24 24" },
        servicesIcon: { svg: `<path d="M12.22 2h-4.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.44.25a2 2 0 0 1-2 1.73V11a2 2 0 0 0 2 2h.18a2 2 0 0 1 1.73 1l.25.44a2 2 0 0 1 1.73 1H20a2 2 0 0 0 2-2v-4.44a2 2 0 0 0-2-2h-.18a2 2 0 0 1-1.73-1l-.25-.44a2 2 0 0 1-1.73-1V4a2 2 0 0 0-2-2z" /><circle cx="12" cy="12" r="3" />`, viewBox: "0 0 24 24" },
        certificatesIcon: { svg: `<path d="M12 21l-8-4.5v-9l8 4.5 8-4.5v9L12 21z"/><path d="M12 12l8-4.5"/><path d="M12 12V3"/><path d="M12 12L4 7.5"/><path d="M16 5.25l-8 4.5"/>`, viewBox: "0 0 24 24" },
        testimonialsIcon: { svg: `<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>`, viewBox: "0 0 24 24" },
        teamIcon: { svg: `<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path>`, viewBox: "0 0 24 24" },
        usersIcon: { svg: `<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>`, viewBox: "0 0 24 24" },
        invitesIcon: { svg: `<path d="M22 17a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h11"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/><path d="M19 14v6"/><path d="M16 17h6"/>`, viewBox: "0 0 24 24" },
        databaseIcon: { svg: `<ellipse cx="12" cy="5" rx="9" ry="3"></ellipse><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"></path><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"></path>`, viewBox: "0 0 24 24" },
        settingsIcon: { svg: `<circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>`, viewBox: "0 0 24 24" },
        developerIcon: { svg: `<polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline>`, viewBox: "0 0 24 24" },
        devSettingsIcon: { svg: '<line x1="4" y1="21" x2="4" y2="14"></line><line x1="4" y1="10" x2="4" y2="3"></line><line x1="12" y1="21" x2="12" y2="12"></line><line x1="12" y1="8" x2="12" y2="3"></line><line x1="20" y1="21" x2="20" y2="16"></line><line x1="20" y1="12" x2="20" y2="3"></line><line x1="1" y1="14" x2="7" y2="14"></line><line x1="9" y1="8" x2="15" y2="8"></line><line x1="17" y1="16" x2="23" y2="16"></line>', viewBox: "0 0 24 24" },
        logoutIcon: { svg: `<path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>`, viewBox: "0 0 24 24" },
    },
    maxProjectImageSizeMB: 2,
    siteConfig: JSON.stringify({
        "projectName": "Architect Firm CMS",
        "version": "1.0.0",
        "defaultCacheTime": 3600
    }, null, 2),
    enableAiFeatures: true,
    enableJournal: true,
};

export const SAMPLE_ABOUT_DATA: Omit<AboutContent, 'id'> = {
    title: "About Our Firm",
    imageUrl: "https://picsum.photos/seed/about-us-main/1200/800",
    sections: [
        {
            title: "Our Philosophy",
            content: "<p>Founded in 2010, ARCH. emerged from a shared passion for minimalist design and a profound respect for the built environment. We are a collective of architects, designers, and thinkers dedicated to creating spaces that are not only aesthetically pleasing but also deeply functional and sustainable. Our philosophy is rooted in the idea that simplicity is the ultimate sophistication.</p>"
        },
        {
            title: "Our Vision & Mission",
            content: "<p>To be a leading voice in contemporary architecture, recognized for our commitment to design excellence, innovation, and creating spaces that enhance the human experience. Our mission is to translate our clients' visions into tangible realities through a collaborative and rigorous design process, focusing on clean lines, honest materials, and natural light.</p>"
        }
    ]
};

export const SAMPLE_SERVICES_DATA: Omit<Service, 'id' | 'slug'>[] = [
    {
        title: "Architectural Design",
        imageUrl: "https://picsum.photos/seed/service-arch/800/600",
        description: "From initial concept to final construction, we provide comprehensive architectural services that bring your vision to life with precision and creativity.",
        sections: [
            { title: "Conceptualization", content: "<p>We work closely with you to understand your needs, aspirations, and the unique potential of your site. This collaborative phase involves sketches, models, and feasibility studies to establish a strong design direction.</p>" },
            { title: "Design Development", content: "<p>Our team refines the initial concepts into detailed architectural plans. We focus on spatial organization, material selection, and integrating sustainable practices to create a cohesive and responsible design.</p>" },
            { title: "Construction Documents", content: "<p>We produce a comprehensive set of drawings and specifications that serve as the blueprint for construction, ensuring every detail is executed to the highest standard.</p>" }
        ],
        order: 0
    },
    {
        title: "Interior Design",
        imageUrl: "https://picsum.photos/seed/service-int/800/600",
        description: "Our interior design services create harmonious and functional spaces that reflect your personality and lifestyle, with meticulous attention to detail.",
        sections: [
            { title: "Space Planning", content: "<p>We optimize the layout and flow of your interior spaces to enhance functionality and comfort. Every element is thoughtfully considered to create a seamless living experience.</p>" },
            { title: "Material & Finish Selection", content: "<p>We guide you through the selection of materials, finishes, furniture, and lighting to create a palette that is both beautiful and durable, ensuring a timeless aesthetic.</p>" }
        ],
        order: 1
    }
];

export const SAMPLE_ASSOCIATES_DATA: Omit<Associate, 'id' | 'slug'>[] = [
    {
        name: "ANSSB",
        logoUrl: "https://picsum.photos/seed/anssb-logo/200/200",
        description: "Specializing in sustainable urban infrastructure and master planning.",
        aboutUs: "<h3>About ANSSB</h3><p>Founded in 1998, ANSSB has been at the forefront of large-scale urban development projects. Our mission is to create resilient and sustainable cities for the future, integrating green technologies with human-centric design principles. We have collaborated on numerous award-winning infrastructure projects across the nation.</p>",
        vision: "To be the leading global partner in creating sustainable and innovative urban environments that enrich communities and respect the planet.",
        mission: "To deliver excellence in engineering and design through collaboration, cutting-edge technology, and a steadfast commitment to our clients' success and the public good.",
        services: "<ul><li>Master Planning</li><li>Infrastructure Design</li><li>Sustainable Development Consulting</li><li>Project Management</li></ul>",
        address: "ANSSB Tower, 123 Future Avenue, Metropolis, 12345",
        email: "contact@anssb.com",
        companyProfileUrl: ""
    },
    {
        name: "NSID",
        logoUrl: "https://picsum.photos/seed/nsid-logo/200/200",
        description: "A creative studio focused on bespoke interior design for luxury residences and hospitality.",
        aboutUs: "<h3>Interior Innovation</h3><p>NSID is an interior design powerhouse known for its bold, sophisticated, and timeless spaces. We believe that a well-designed interior can profoundly impact quality of life. Our portfolio includes luxury hotels, high-end residential towers, and exclusive private homes, each crafted with meticulous attention to detail and a unique creative vision.</p>",
        vision: "To redefine luxury living through exceptionally crafted interiors that tell a unique story and evoke powerful emotions.",
        mission: "To create bespoke, harmonious, and functional interior spaces that reflect the individuality of our clients, pushing the boundaries of design and quality.",
        services: "<ul><li>Luxury Residential Design</li><li>Hospitality & Hotel Design</li><li>Custom Furniture & Lighting</li><li>Art Curation</li></ul>",
        address: "The Design Suite, 456 Style Boulevard, Metropolis, 54321",
        email: "studio@nsid.design",
        companyProfileUrl: ""
    }
];

export const SAMPLE_PAGES_DATA: Omit<Page, 'id' | 'slug'>[] = [
    {
        title: "Our Philosophy",
        content: `
            <p>This is a sample generic page demonstrating how you can add additional content to your site. You can edit this in the 'Pages' section of the admin panel.</p>
            <h3>Our Approach</h3>
            <p>We believe in a collaborative design process that involves our clients at every stage. We are committed to creating architecture that is not only beautiful but also responsible, considering its impact on the environment and the community.</p>
        `,
        published: true,
        order: 1,
        layoutTemplate: 'standard',
        featureImageUrl: "https://picsum.photos/seed/philosophy-page/1200/800",
        seoTitle: "Our Philosophy | ARCH.",
        seoDescription: "Learn about ARCH.'s design philosophy and our approach to creating minimalist, functional, and sustainable architectural spaces."
    }
];

export const SAMPLE_CERTIFICATES_DATA: Omit<Certificate, 'id'>[] = [
    {
        title: "Green Building Certification",
        issuingBody: "Global Sustainability Council",
        year: 2023,
        imageUrl: "https://picsum.photos/seed/cert1/600/400",
        order: 0,
    },
    {
        title: "Architectural Excellence Award",
        issuingBody: "National Institute of Architects",
        year: 2022,
        imageUrl: "https://picsum.photos/seed/cert2/600/400",
        order: 1,
    }
];

export const SAMPLE_TESTIMONIALS_DATA: Omit<Testimonial, 'id'>[] = [
    {
        clientName: "The Chen Family",
        companyName: "Private Client",
        quote: "Working with ARCH. was a transformative experience. They turned our vision for The Serenity House into a breathtaking reality that we get to call home.",
        projectSlug: "the-serenity-house",
        order: 0,
    },
    {
        clientName: "Jane Doe, CEO",
        companyName: "Aperture Inc.",
        quote: "The team's professionalism and creative vision for our new corporate campus were unparalleled. They've created a workspace that truly inspires innovation.",
        projectSlug: "aperture-corporate-campus",
        order: 1,
    }
];

export const SAMPLE_VIDEOS_DATA: Omit<Video, 'id'>[] = [
    {
        title: "The Art of Architectural Visualization",
        youtubeVideoId: "iAcAdn_nL_I",
        description: "A stunning showcase of how we bring architectural concepts to life through cinematic animation.",
        order: 0,
        isHero: true,
    },
    {
        title: "Minimalist Home Design Tour",
        youtubeVideoId: "CLF2s-kx_L4",
        description: "Explore the serene interiors and clean lines of one of our signature residential projects.",
        order: 1,
        isHero: false,
    },
    {
        title: "From Sketch to Structure: The Campus Project",
        youtubeVideoId: "Qc7_YcdwkeA",
        description: "A behind-the-scenes look at the design and construction process of a large-scale corporate campus.",
        order: 2,
        isHero: false,
    }
];


export const SAMPLE_PROJECT_DATA: Omit<Project, 'id' | 'slug'>[] = [
  {
    title: "The Serenity House",
    category: "Residential",
    year: 2023,
    description: "A minimalist retreat designed to blend seamlessly with its natural surroundings, emphasizing tranquility and light.",
    images: [{ url: "https://picsum.photos/seed/serenity/1200/800" }, { url: "https://picsum.photos/seed/serenity2/1200/800" }],
    details: "<h3>Concept</h3><p>The Serenity House project was conceived as a sanctuary from the bustling city life. The primary goal was to create a space that fosters a deep connection with nature. We utilized a palette of natural materials, including locally sourced stone and sustainable timber, to create a warm and inviting atmosphere.</p><h3>Design</h3><p>Large, floor-to-ceiling windows were a key feature, blurring the lines between indoors and outdoors. The open-plan living area is oriented to maximize natural light and offers panoramic views of the surrounding forest. Every detail, from the custom cabinetry to the integrated lighting, was meticulously planned to maintain a clean, uncluttered aesthetic.</p>",
    client: "The Chen Family",
    location: "Willow Creek, CA",
    status: "Completed",
    price: 3500000,
    published: true,
    featured: true,
    order: 0
  },
  {
    title: "Aperture Corporate Campus",
    category: "Corporate",
    year: 2024,
    description: "An innovative workspace designed for collaboration and creativity, featuring modular structures and green rooftops.",
    images: [{ url: "https://picsum.photos/seed/aperture/1200/800" }, { url: "https://picsum.photos/seed/aperture2/1200/800" }],
    details: "<h3>A Hub for Innovation</h3><p>Aperture's new corporate campus was designed to reflect its forward-thinking culture. We created a series of interconnected buildings that encourage spontaneous interaction and collaboration among employees. The design incorporates flexible workspaces, state-of-the-art technology, and ample natural light to foster a dynamic and productive environment.</p><p>Sustainability was a core principle, with features like rainwater harvesting, solar panels, and extensive green roofs that reduce the building's carbon footprint and provide a beautiful, natural setting for employees to enjoy.</p>",
    client: "Aperture Inc.",
    location: "Silicon Valley, CA",
    status: "Completed",
    published: true,
    featured: true,
    associateSlug: "anssb",
    order: 1
  },
  {
    title: "The Aethelred Museum",
    category: "Cultural",
    year: 2022,
    description: "A modern cultural institution that respects its historic context while providing a contemporary space for art.",
    images: [{ url: "https://picsum.photos/seed/museum/1200/800" }, { url: "https://picsum.photos/seed/museum2/1200/800" }],
    details: "<h3>Bridging Past and Present</h3><p>The design for The Aethelred Museum involved the careful restoration of a heritage building and the addition of a bold, contemporary wing. The new structure, characterized by its clean lines and minimalist material palette, stands in respectful dialogue with the original architecture. The interior galleries are designed to be flexible, accommodating a wide range of exhibitions, from classical sculptures to large-scale modern installations.</p>",
    client: "City of Aethelred",
    location: "Aethelred, UK",
    status: "Completed",
    price: 12000000,
    published: true,
    featured: false,
    order: 2
  },
  {
    title: "Orion Transit Hub",
    category: "Infrastructure",
    year: 2025,
    description: "A visionary transportation hub designed for efficiency and passenger experience, featuring a dramatic, light-filled canopy.",
    images: [{ url: "https://picsum.photos/seed/orion/1200/800" }, { url: "https://picsum.photos/seed/orion2/1200/800" }],
    details: "<h3>Gateway to the Future</h3><p>The Orion Transit Hub is more than just a train station; it's a new civic landmark. The design is defined by its soaring, ETFE-clad roof structure, which floods the main concourse with natural light. The layout prioritizes intuitive wayfinding and seamless connections between different modes of transport, creating a stress-free experience for millions of commuters. The project is currently under construction and is set to become a benchmark for future public infrastructure projects.</p>",
    client: "Metropolitan Transit Authority",
    location: "Orion City",
    status: "Under Construction",
    published: true,
    featured: false,
    associateSlug: "anssb",
    order: 3
  },
  {
    title: "The Coastal Watchtower",
    category: "Residential",
    year: 2023,
    description: "A vertically-oriented home on a cliffside, offering breathtaking panoramic views of the ocean.",
    images: [{ url: "https://picsum.photos/seed/coast/800/1200" }, { url: "https://picsum.photos/seed/coast2/800/1200" }],
    details: "<h3>Living on the Edge</h3><p>Perched on a dramatic cliffside, the Coastal Watchtower is a unique residential project designed to maximize its stunning location. The house is organized vertically, with living spaces on the upper floors to capture the best views. A robust concrete structure anchors the building to the rock, while warm wood interiors create a cozy and protected feel against the elements. Expansive glazing on the ocean-facing side makes the sea a constant, mesmerizing presence.</p>",
    client: "Private Client",
    location: "Big Sur, CA",
    status: "Completed",
    published: true,
    featured: true,
    associateSlug: "nsid",
    order: 4
  },
  {
    title: "Elysian Fields Pavilion",
    category: "Cultural",
    year: 2024,
    description: "A temporary, parametrically designed pavilion for a public park, exploring the intersection of nature and technology.",
    images: [{ url: "https://picsum.photos/seed/pavilion/1200/800" }, { url: "https://picsum.photos/seed/pavilion2/1200/800" }],
    details: "<h3>Nature Reimagined</h3><p>The Elysian Fields Pavilion is an experimental structure that uses computational design to mimic natural forms. Its complex, latticed canopy filters sunlight to create a dappled light effect, reminiscent of a forest floor. The pavilion is designed to be a place of rest and contemplation, offering a unique spatial experience that changes throughout the day as the sun moves across the sky. It serves as a community gathering space and a showcase for innovative construction techniques.</p>",
    client: "Elysian Park Foundation",
    location: "Metropolis",
    status: "Concept",
    published: true,
    featured: false,
    order: 5
  }
];